// ─── Sprint 093 — Operator Persona (updated Build 2.07.0) ────────────────────
// Extended operator identity.
// v1: displayName, avatar, timezone, country, incomeGoalMonthly, preferredCategories
// v2: adds homeBase, currentBase, travelStyle, workDevices, hoursAvailable, dailyGoalUSD, lastLogin

export type TravelStyle = 'planning' | 'occasional' | 'nomad';
export type WorkDevice  = 'desktop' | 'mobile';

// ─── Phase 4 (Build 2.09.0) — Roaming Profile ────────────────────────────────
// Finer-grained roaming style chosen during the Activation Experience.
// Used only for future recommendations — no hidden logic.
export type RoamingStyle =
  | 'planning-future'  // Planning Future Travel
  | 'weekend'          // Weekend Traveler
  | 'slow'             // Slow Traveler
  | 'long-stay'        // Long Stay Nomad
  | 'full-time';       // Full-Time Roamer

/** Map the v3 roaming style onto the legacy 3-value travelStyle for compat. */
export function roamingStyleToTravelStyle(style: RoamingStyle): TravelStyle {
  switch (style) {
    case 'planning-future': return 'planning';
    case 'weekend':         return 'occasional';
    default:                return 'nomad';
  }
}

/**
 * Lightweight location snapshot stored inside the persona.
 * Mirrors the key fields of RoamerLocation without the runtime-only fields
 * (lastUpdated, source, aliases).  Enough to reconstruct a RoamerLocation
 * and to display "Home Base / Current Base" anywhere in the UI.
 */
export interface LocationBase {
  locationId: string;
  city: string;
  region: string;
  state: string;
  country: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  timezone: string;
  formattedAddress: string;
}

export interface OperatorPersona {
  /** Operator's chosen display name. */
  displayName: string;

  /**
   * Profile photo as a base64 data URL (e.g. "data:image/jpeg;base64,...").
   * Null if no photo has been uploaded.
   */
  avatarDataUrl: string | null;

  /** IANA timezone string, e.g. "America/New_York". */
  timezone: string;

  /** Country of residence (legacy v1 field — prefer currentBase.country). */
  country: string;

  /** Monthly income goal in USD. */
  incomeGoalMonthly: number;

  /** Daily income goal in USD. Default 25. */
  dailyGoalUSD: number;

  /** Preferred opportunity categories (mirrors OperatorProfile.opportunityCategories). */
  preferredCategories: string[];

  /** Operator's permanent home city — never changes automatically. */
  homeBase: LocationBase | null;

  /** Operator's current location — may change when they travel. */
  currentBase: LocationBase | null;

  /** How the operator travels. Affects recommendations. */
  travelStyle: TravelStyle | null;

  /** Devices available for work. */
  workDevices: WorkDevice[];

  /** Hours per day available for work. */
  hoursAvailable: number;

  /** Whether the first-time onboarding wizard has been completed. */
  onboardingCompleted: boolean;

  // ─── Trust & Transparency (Trust Sprint) ──────────────────────────────────

  /** Version of Terms of Service the operator accepted (e.g. '1.0'). */
  termsAcceptedVersion?: string;

  /** Version of Privacy Policy the operator accepted (e.g. '1.0'). */
  privacyAcceptedVersion?: string;

  /** ISO timestamp when the operator accepted the current terms. */
  termsAcceptedAt?: string;

  /** ISO timestamp of initial persona creation. */
  createdAt: string;

  /** ISO timestamp of last update. */
  updatedAt: string;

  /** ISO timestamp of last successful login. Null until first login recorded. */
  lastLogin: string | null;

  // ─── Phase 4 (Build 2.09.0) — Activation Experience fields ────────────────

  /** Optional short nickname used in greetings ("Morning, Jay."). */
  nickname: string | null;

  /** Destinations the operator hopes to visit (Dream Bases). NEVER become currentBase. */
  dreamBases: LocationBase[];

  /** Fine-grained roaming profile chosen during activation. Recommendations only. */
  roamingStyle: RoamingStyle | null;

  /** Weekly income goal in USD (derived at activation; editable later). */
  weeklyGoalUSD: number;

  /** ISO timestamp of the FIRST completed activation. Null until activated. */
  activationDate: string | null;

  /** ISO timestamp of the most recent activation (replays update this). */
  lastActivation: string | null;

  /** Whether the operator has landed on Home at least once after activation. */
  firstLoginComplete: boolean;

  /** Version of the onboarding experience that created/last activated this persona. */
  onboardingVersion: string | null;

  /** Schema version for future migrations. */
  personaVersion: '1' | '2' | '3';
}

/** Onboarding experience version written by the Phase 4 Activation Experience. */
export const ONBOARDING_VERSION = '4';

/** Timezone options shown in the onboarding wizard. */
export const TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'America/New_York (ET)', value: 'America/New_York' },
  { label: 'America/Chicago (CT)', value: 'America/Chicago' },
  { label: 'America/Denver (MT)', value: 'America/Denver' },
  { label: 'America/Los_Angeles (PT)', value: 'America/Los_Angeles' },
  { label: 'America/Anchorage (AKT)', value: 'America/Anchorage' },
  { label: 'America/Honolulu (HT)', value: 'America/Honolulu' },
  { label: 'America/Sao_Paulo (BRT)', value: 'America/Sao_Paulo' },
  { label: 'America/Toronto (ET)', value: 'America/Toronto' },
  { label: 'America/Vancouver (PT)', value: 'America/Vancouver' },
  { label: 'Europe/London (GMT/BST)', value: 'Europe/London' },
  { label: 'Europe/Dublin (IST)', value: 'Europe/Dublin' },
  { label: 'Europe/Paris (CET)', value: 'Europe/Paris' },
  { label: 'Europe/Berlin (CET)', value: 'Europe/Berlin' },
  { label: 'Europe/Amsterdam (CET)', value: 'Europe/Amsterdam' },
  { label: 'Europe/Warsaw (CET)', value: 'Europe/Warsaw' },
  { label: 'Europe/Kiev (EET)', value: 'Europe/Kiev' },
  { label: 'Europe/Moscow (MSK)', value: 'Europe/Moscow' },
  { label: 'Africa/Johannesburg (SAST)', value: 'Africa/Johannesburg' },
  { label: 'Africa/Lagos (WAT)', value: 'Africa/Lagos' },
  { label: 'Africa/Nairobi (EAT)', value: 'Africa/Nairobi' },
  { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'Asia/Dhaka (BST)', value: 'Asia/Dhaka' },
  { label: 'Asia/Bangkok (ICT)', value: 'Asia/Bangkok' },
  { label: 'Asia/Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Asia/Manila (PHT)', value: 'Asia/Manila' },
  { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Asia/Seoul (KST)', value: 'Asia/Seoul' },
  { label: 'Australia/Sydney (AEDT)', value: 'Australia/Sydney' },
  { label: 'Australia/Melbourne (AEDT)', value: 'Australia/Melbourne' },
  { label: 'Australia/Perth (AWST)', value: 'Australia/Perth' },
  { label: 'Pacific/Auckland (NZDT)', value: 'Pacific/Auckland' },
  { label: 'Pacific/Fiji (FJT)', value: 'Pacific/Fiji' },
] as const;
